/**
 * <phi-back-button>
 * ─────────────────────────────────────────────────────────────
 * Custom element that renders a Hermès-style "← Back" button.
 *
 * Behaviour
 * ---------
 * 1. If there is browser history (history.length > 1), clicking always
 *    calls history.back() so the user returns to the previous page.
 * 2. Only when there is no history (e.g. opened directly or new tab),
 *    it goes to the URL in the `href` attribute (back_link setting).
 *
 * Usage in Liquid
 * ---------------
 *   <phi-back-button href="{{ section.settings.back_link }}"></phi-back-button>
 *
 * Attributes
 * ----------
 *   href   – fallback URL when there is no history to go back to
 */

class PhiBackButton extends HTMLElement {
    connectedCallback() {
        /* ── Render ──────────────────────────────────────────────── */
        const fallback = this.getAttribute('href') || '/';
        const label = this.getAttribute('label') || 'Back';

        this.innerHTML = `
      <button
        class="phi-back-btn"
        role="link"
        aria-label="${label}"
        data-href="${fallback}"
      >
     <svg  viewBox="0 0 20 15" xmlns="http://www.w3.org/2000/svg">
  <path d="M10 1 L3 7.5 L10 14 M3 7.5 L18 7.5"
        stroke="black"
        stroke-width="2"
        fill="none"/>
</svg>
        <span class="phi-back-btn__text phi-sr-only" data-nosnippet="true"
              aria-hidden="false">${label}</span>
        <span class="phi-back-btn__text--desktop" aria-hidden="true">${label}</span>
      </button>
    `;

        /* ── Event ───────────────────────────────────────────────── */
        this.querySelector('.phi-back-btn').addEventListener('click', () => {
            /* Prefer browser back when there is history; use link only when there is not */
            if (history.length > 1) {
                history.back();
            } else {
                window.location.href = fallback;
            }
        });

        /* ── Keyboard: Space / Enter ─────────────────────────────── */
        this.querySelector('.phi-back-btn').addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.click();
            }
        });
    }
}

if (!customElements.get('phi-back-button')) {
    customElements.define('phi-back-button', PhiBackButton);
}
